import {Box, Card, CircularProgress} from "@mui/material";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate, useSearchParams} from "react-router";
import {useDispatch, useSelector} from "react-redux";
import ActiveAndClosedButton from "./activeAndClosedButton.tsx";
import Filters from "./filter/index.tsx";
import TicketListHeader from "./ticketListHeader.tsx";
import NoTicketsError from "./noTicketsError.tsx";
import TicketsCard from "./ticketsCard.tsx";
import {AppDispatch, RootState} from "@store/store.tsx";
import {
    fetchTickets,
    fetchTotalTickets,
    updateTicketStatus,
} from "@store/ticketListSlice.tsx";
import ResolvedModal from "../../../Chat/components/resolvedModal.tsx";
import ResolvedDrawer from "../../../Chat/components/resolvedDrawer.tsx";
import AddUserModal from "@common/components/AddUser/addUserModal.tsx";


const TicketList: React.FC = () => {

    const [resolvedDrawer, setResolvedDrawer] = useState<boolean>(false);
    const [filterValue, setFilterValue] = useState<string>("");
    const [initial, setInitial] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState("");
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [addUserModal, setAddUserModal] = useState<boolean>(false);
    const [sortByValue, setSortByValue] = useState("updated_at");
    const [sortByOrder, setSortByOrder] = useState("desc");
    const [hideResolved, setHideResolved] = useState(false);
    const [updateSearchValue, setUpdateSearchValue] = useState("");
    const [ticketIdAndStatus, setTicketIdAndStatus] = useState<{
        ticketId: number;
        status: number;
        tag: string;
        orgId: number | undefined;
    }>({ticketId: 0, status: 0, tag: "", orgId: undefined});
    const open = Boolean(anchorEl);
    const scrollRef = useRef(null);
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const {customStatuses} = useSelector((state: RootState) => state.config);
    const {active, closed, loading, retrigger, organizations} = useSelector(
        (state: RootState) => state.tickets
    );
    const [searchParams] = useSearchParams();

    const status =
        (searchParams.get("status") as "active" | "closed") || "active";

    let activeTicketData: any = undefined;
    let closedTicketData: any = undefined;
    if (status === "active") {
        activeTicketData = [
            ...active.live_perfusion.data,
            ...active.post_perfusion.data,
            ...active.general.data,
        ];
    } else {
        closedTicketData = [
            ...closed.live_perfusion.data,
            ...closed.post_perfusion.data,
            ...closed.general.data,
        ];
    }

    useEffect(() => {
        if (status) {
            navigate(`/?status=${status}`);
        }
        setInitial(true);
    }, [initial]);
    const handleClick = (
        event: React.MouseEvent<HTMLElement>,
        label: string,
        data?: any
    ) => {
        if (label === "resolved") {
            const customStatus = customStatuses.find((status) => status.endUserLabel === "Resolved")
            setAnchorEl(event.currentTarget);
            let tagValue = "general";
            if (data.tags.includes("live_perfusion")) {
                tagValue = "live_perfusion";
            } else if (data.tags.includes("post_perfusion")) {
                tagValue = "post_perfusion";
            }
            setTicketIdAndStatus({
                ticketId: data?.id,
                status: customStatus?.id,
                tag: tagValue,
                orgId: data.organizationId,
            });
        }
        if (data.customStatusId === "25271088415249") {
            setHideResolved(true);
        } else {
            setHideResolved(false);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleTicketNavigation = (ticketId?: number) => {
        navigate(`/chat/${ticketId}?status=${status}`);
    };

    useEffect(() => {
        if (initial)
            dispatch(
                fetchTickets({
                    type: status,
                    query: updateSearchValue,
                    reset: true,
                    status: filterValue,
                    sortBy: sortByValue,
                    order: sortByOrder,
                })
            );
        dispatch(
            fetchTotalTickets({
                type: status,
                status: filterValue,
                query: updateSearchValue,
            })
        );
    }, [
        status,
        updateSearchValue,
        filterValue,
        sortByValue,
        sortByOrder,
        initial,
    ]);

    useEffect(() => {
        if (retrigger) {
            dispatch(
                fetchTickets({
                    type: status,
                    query: updateSearchValue,
                    status: filterValue,
                    sortBy: sortByValue,
                    order: sortByOrder,
                })
            );
        }
    }, [retrigger]);

    const onIntersection = useCallback(
        ([entry]: any) => {
            const hasMore =
                status === "active"
                    ? activeTicketData.length !== active?.totalCount
                    : closed?.totalCount !== closedTicketData.length;
            if (entry.isIntersecting && loading !== status && hasMore) {
                dispatch(
                    fetchTickets({
                        type: status,
                        query: updateSearchValue,
                        status: filterValue,
                        sortBy: sortByValue,
                        order: sortByOrder,
                    })
                );
                setInitial(true);
            }
        },
        [loading, status, active, closed, updateSearchValue]
    );

    useEffect(() => {
        const currentRef = scrollRef.current;
        if (!currentRef) return;
        const observer = new IntersectionObserver(onIntersection, {
            threshold: 0.1,
        });
        observer.observe(currentRef);
        return () => {
            if (currentRef) observer.unobserve(currentRef);
        };
    }, [
        updateSearchValue,
        filterValue,
        sortByValue,
        sortByOrder,
        onIntersection,
    ]);

    const handleActiveTickets = (status: "active" | "closed") => {
        navigate(`/?status=${status}`);
    };

    const handleTicketAction = (action: string) => {
        if (action === "resolved") {
            setResolvedDrawer(true);
        } else {
            setAddUserModal(true);
        }
    };

    const handleTicketResolvedAction = () => {
        const payload = {
            customTicketStatusId: ticketIdAndStatus.status,
            tag: ticketIdAndStatus.tag,
            statusName: "solved",
        };
        if (ticketIdAndStatus) {
            dispatch(
                updateTicketStatus({ticketId: ticketIdAndStatus.ticketId, payload})
            );
            setAnchorEl(null);
            setResolvedDrawer(false);
        }
    };

    return (
        <>
            <ActiveAndClosedButton
                handleActiveTickets={handleActiveTickets}
                activeTickets={status}
            />
            <Card>
                <Box component="div" className="pt-2 px-2 ">
                    <Box
                        className="flex flex-row justify-between border-dotted border-b-2 md:border-[#0000004D] border-[#666666]">
                        <TicketListHeader
                            activeTotalCount={active?.totalCount}
                            closedTotalCount={closed?.totalCount}
                            activeTickets={status}
                        />

                        <Box className="hidden md:flex">
                            <Filters
                                setFilterValue={setFilterValue}
                                setSearchValue={setSearchValue}
                                setSortByValue={setSortByValue}
                                setSortByOrder={setSortByOrder}
                                setUpdateSearchValue={setUpdateSearchValue}
                                updateSearchValue={updateSearchValue}
                                filterValue={filterValue}
                                searchValue={searchValue}
                                sortByValue={sortByValue}
                                sortByOrder={sortByOrder}
                            />
                        </Box>
                    </Box>
                </Box>

                <Box className="flex  flex-col md:hidden">
                    <Filters
                        setFilterValue={setFilterValue}
                        setSearchValue={setSearchValue}
                        setSortByValue={setSortByValue}
                        setSortByOrder={setSortByOrder}
                        setUpdateSearchValue={setUpdateSearchValue}
                        updateSearchValue={updateSearchValue}
                        filterValue={filterValue}
                        searchValue={searchValue}
                        sortByValue={sortByValue}
                        sortByOrder={sortByOrder}
                    />
                </Box>

                {(activeTicketData?.length === 0 || closedTicketData?.length === 0) &&
                    loading !== status && <NoTicketsError/>}

                <Box
                    className="grid grid-cols-1 divide-y"
                    sx={{
                        maxHeight: {xs: "calc(100vh - 412px)", sm: "calc(100vh - 290px)"},
                        overflowY: "auto",
                    }}
                >
                    <TicketsCard
                        activeTickets={status}
                        ticketsData={
                            status === "active" ? activeTicketData : closedTicketData
                        }
                        customStatuses={customStatuses}
                        organizations={organizations}
                        handleClick={handleClick}
                        handleClose={handleClose}
                        handleTicketAction={handleTicketAction}
                        open={open}
                        anchorEl={anchorEl}
                        handleTicketNavigation={handleTicketNavigation}
                        hideResolved={hideResolved}
                    />

                    {loading === status && (
                        <Box className="h-[5px] py-8 flex justify-center items-center">
                            <CircularProgress size="20px"/>
                        </Box>
                    )}
                    <Box ref={scrollRef}></Box>
                </Box>
            </Card>
            <ResolvedModal
                resolvedDrawer={resolvedDrawer}
                setResolvedDrawer={setResolvedDrawer}
                handleTicketResolvedAction={handleTicketResolvedAction}
            />
            <ResolvedDrawer
                resolvedDrawer={resolvedDrawer}
                setResolvedDrawer={setResolvedDrawer}
                handleTicketResolvedAction={handleTicketResolvedAction}
            />
            <AddUserModal
                addUserModal={addUserModal}
                setAddUserModal={setAddUserModal}
                ticketId={ticketIdAndStatus.ticketId}
            />
        </>
    );
};

export default TicketList;
